import React, { useEffect, useState } from 'react'
import { TextField, Button, Typography, Box, Container, Alert, InputAdornment, IconButton } from '@mui/material'
import { userSignIn } from '../../../../utils/Firebase/methods'
import { handleFirebaseAuthError } from '../../../../utils/Firebase/errorHandler'
import CircularProgress from '@mui/material/CircularProgress'
import { Trans } from 'react-i18next'
import { Info, Eye, EyeOff } from 'react-feather'
import { alertStyle } from './LoginForm.styles'

type LoginFormProps = {
	navigate: () => void
	switchToCreate: () => void
	prefillEmail?: string
}

export default function LoginForm({ navigate, switchToCreate, prefillEmail }: LoginFormProps) {
	const [email, setEmail] = useState<string>('')
	const [password, setPassword] = useState<string>('')
	const [error, setError] = useState<string>('')
	const [loading, setLoading] = useState<boolean>(false)
	const [showPassword, setShowPassword] = useState<boolean>(false)
	const queryParams = new URLSearchParams(window.location.search)

	const handleTogglePasswordVisibility = () => {
		setShowPassword((prevShowPassword) => !prevShowPassword)
	}

	const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault()
		setLoading(true)
		setError('')
		try {
			await userSignIn(email, password)
		} catch (error: any) {
			const userFriendlyMessage = handleFirebaseAuthError(error)
			setError(userFriendlyMessage)
			console.error('Login failed:', error)
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		setEmail(prefillEmail || queryParams.get('email') || '')
	}, [prefillEmail])

	return (
		<Container component="main" maxWidth="xs">
			{(queryParams.get('email') || prefillEmail) && (
				<Alert icon={<Info size={20} />} severity="info" sx={alertStyle}>
					<Trans i18nKey="login.existing_account_notice" />
				</Alert>
			)}
			<Box component="form" onSubmit={handleLogin} sx={{ mt: 1 }}>
				<TextField
					type="email"
					variant="outlined"
					margin="normal"
					required
					fullWidth
					label="Email Address"
					autoComplete="email"
					value={email}
					onChange={(e) => setEmail(e.target.value)}
				/>
				<TextField
					variant="outlined"
					margin="normal"
					required
					fullWidth
					label="Password"
					type={showPassword ? 'text' : 'password'}
					value={password}
					onChange={(e) => setPassword(e.target.value)}
					InputProps={{
						endAdornment: (
							<InputAdornment position="end">
								<IconButton
									aria-label="toggle password visibility"
									onClick={handleTogglePasswordVisibility}
									edge="end"
								>
									{showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
								</IconButton>
							</InputAdornment>
						),
					}}
				/>
				<Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }} disabled={loading}>
					{loading ? <CircularProgress style={{ height: '25px', width: '25px' }} /> : 'Sign In'}
				</Button>
				{error && <Typography color="error">{error}</Typography>}
				<Button onClick={switchToCreate} fullWidth color="primary">
					<Trans i18nKey="login.sign_up" />
				</Button>
				<Button onClick={navigate} fullWidth color="primary">
					<Trans i18nKey="login.password_recovery" />
				</Button>
			</Box>
		</Container>
	)
}
